import { Dispatch } from "redux";

import { patchCartItems } from "api/requests/patchCartItems";
import { Cart, CartChange, CartContext, CartItem } from "custom-types/Cart";
import { trackCheckoutEvent } from "utils/trackEvent";

import { ScheduleWindow } from "components/Checkout/ScheduledOrderModal/ScheduledOrderModal";

export type CartState = {
  changes: CartChange[];
  convertible: boolean;
  data: Cart | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  error: any | null;
  isCheckingPhone: boolean;
  isLoading: boolean;
  isUpdating: boolean;
  phoneError: string | null;
  selectedForDeliveryOption: boolean;
  showSmsOptInMessage: boolean;
  twilioPhoneNumber: string | null;
  selectedScheduleWindow?: ScheduleWindow;
};

export const initialState: CartState = {
  changes: [],
  convertible: false,
  data: null,
  error: null,
  isCheckingPhone: false,
  isLoading: false,
  isUpdating: false,
  phoneError: null,
  selectedForDeliveryOption: false,
  showSmsOptInMessage: false,
  twilioPhoneNumber: null,
};

export const GET_CART_REQUEST = "GET_CART_REQUEST";
export const GET_CART_SUCCESS = "GET_CART_SUCCESS";
export const GET_CART_FAILURE = "GET_CART_FAILURE";

export const REFRESH_CART_REQUEST = "REFRESH_CART_REQUEST";
export const REFRESH_CART_SUCCESS = "REFRESH_CART_SUCCESS";
export const REFRESH_CART_FAILURE = "REFRESH_CART_FAILURE";

export const UPDATE_CART_REQUEST = "UPDATE_CART_REQUEST";
export const UPDATE_CART_SUCCESS = "UPDATE_CART_SUCCESS";
export const UPDATE_CART_FAILURE = "UPDATE_CART_FAILURE";

export const SAVE_ORDER_REQUEST = "SAVE_ORDER_REQUEST";
export const SAVE_ORDER_SUCCESS = "SAVE_ORDER_SUCCESS";
export const SAVE_ORDER_FAILURE = "SAVE_ORDER_FAILURE";

export const SET_FOR_DELIVERY_OPTION = "SET_FOR_DELIVERY_OPTION";
export const SELECTED_SCHEDULE_WINDOW = "SELECTED_SCHEDULE_WINDOW";

export type SetDeliveryWindow = {
  type: typeof SELECTED_SCHEDULE_WINDOW;
  selectedScheduleWindow?: ScheduleWindow;
};

export type GetCartAction = {
  type: typeof GET_CART_REQUEST;
  isLoading: boolean;
};
export type GetCartSuccessAction = {
  type: typeof GET_CART_SUCCESS;
  cart: Cart;
  changes: CartChange[];
};
export type GetCartFailureAction = {
  type: typeof GET_CART_FAILURE;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  error: any;
};

export type RefreshCartAction = {
  type: typeof REFRESH_CART_REQUEST;
  isLoading: boolean;
  isUpdating: boolean;
};
export type RefreshCartSuccessAction = {
  type: typeof REFRESH_CART_SUCCESS;
  cart: Cart;
  changes: CartChange[];
  isLoading: boolean;
  isUpdating: boolean;
};
export type RefreshCartFailureAction = {
  type: typeof REFRESH_CART_FAILURE;
  error: string;
  isLoading: boolean;
  isUpdating: boolean;
};

export type UpdateCartAction = {
  type: typeof UPDATE_CART_REQUEST;
  isUpdating: boolean;
};
export type UpdateCartSuccessAction = {
  type: typeof UPDATE_CART_SUCCESS;
  cart: Cart;
  changes: CartChange[];
  isLoading: boolean;
  isUpdating: boolean;
};
export type UpdateCartFailureAction = {
  type: typeof UPDATE_CART_FAILURE;
  error: string;
  isLoading: boolean;
  isUpdating: boolean;
};

export type SaveReservationSuccessAction = {
  type: typeof SAVE_ORDER_SUCCESS;
  error: string;
};
export type SaveReservationFailureAction = {
  type: typeof SAVE_ORDER_FAILURE;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  payload: any;
};

export type SetForDeliveryOptionAction = {
  type: typeof SET_FOR_DELIVERY_OPTION;
  forDelivery: boolean;
};

export type CartActions =
  | GetCartAction
  | GetCartSuccessAction
  | GetCartFailureAction
  | RefreshCartAction
  | RefreshCartSuccessAction
  | RefreshCartFailureAction
  | UpdateCartAction
  | UpdateCartSuccessAction
  | UpdateCartFailureAction
  | SaveReservationSuccessAction
  | SaveReservationFailureAction
  | SetDeliveryWindow
  | SetForDeliveryOptionAction;

// Dispatching the custom event "getcart" signals to cephalopod that the cart has changed,
// and that status indicators should be updated.
const refreshCartIconBadge = () => {
  const e = new CustomEvent("getcart");
  document.dispatchEvent(e);
};

export const updateCart = (
  dispatch: Dispatch,
  item: CartItem,
  trackingKeyword: string,
  sessionId: string,
  janeCheckoutEnabled: boolean,
  calculateTax = false,
  cartContext: CartContext | null = null
) => {
  dispatch({
    type: UPDATE_CART_REQUEST,
  });

  patchCartItems({
    calculateTax: calculateTax,
    cartContext: cartContext,
    cartItem: item,
    sessionId: sessionId,
  })
    .then((cart) => {
      refreshCartIconBadge();
      trackCheckoutEvent("Click", trackingKeyword, {
        cartItem: item,
        category: "Checkout Page Cart",
      });
      dispatch({
        cart,
        type: UPDATE_CART_SUCCESS,
      });
    })
    .catch((error: string) => {
      trackCheckoutEvent("Click", "Error", {
        bagId: sessionId,
        category: "Add to Bag",
        errorMessage: error || "Could not update cart",
        janeCheckoutEnabled,
      });
      dispatch({
        error,
        type: UPDATE_CART_FAILURE,
      });
    });
};

export const cartReducer = (
  state = initialState,
  action: CartActions
): CartState => {
  switch (action.type) {
    case GET_CART_REQUEST:
      return {
        ...state,
        isLoading: true,
      };
    case GET_CART_SUCCESS:
      return {
        ...state,
        changes: action.changes,
        data: action.cart,
      };
    case GET_CART_FAILURE:
      return {
        ...state,
        error: action.error,
        isLoading: false,
      };
    case REFRESH_CART_REQUEST:
      return {
        ...state,
        error: initialState.error,
        isLoading: true,
        isUpdating: true,
      };
    case REFRESH_CART_SUCCESS:
      return {
        ...state,
        changes: action.changes,
        data: action.cart,
        isLoading: false,
        isUpdating: false,
      };
    case REFRESH_CART_FAILURE:
      return {
        ...state,
        error: action.error,
        isLoading: false,
        isUpdating: false,
      };
    case UPDATE_CART_REQUEST:
      return {
        ...state,
        isUpdating: true,
      };
    case UPDATE_CART_SUCCESS:
      return {
        ...state,
        data: action.cart,
        isLoading: false,
        isUpdating: false,
      };
    case UPDATE_CART_FAILURE:
      return {
        ...state,
        error: action.error,
        isLoading: false,
        isUpdating: false,
      };
    case SAVE_ORDER_SUCCESS:
      return {
        ...state,
        error: initialState.error,
      };
    case SAVE_ORDER_FAILURE: {
      const errors = action.payload;
      if (errors.length > 0) {
        return { ...state, error: errors };
      }
      return state;
    }
    case SET_FOR_DELIVERY_OPTION:
      return {
        ...state,
        selectedForDeliveryOption: action.forDelivery,
      };
    case SELECTED_SCHEDULE_WINDOW:
      return {
        ...state,
        selectedScheduleWindow: action.selectedScheduleWindow,
      };

    default:
      return state;
  }
};

export default cartReducer;
